import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Mail, Save, Eye, Code, Building2 } from 'lucide-react';

export default function EmailTemplatesPage() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('TICKET_CREATED');
  const [template, setTemplate] = useState({ name: '', subject: '', htmlContent: '' });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  const events = [
    { value: 'TICKET_CREATED', label: 'Ticket Criado' },
    { value: 'STATUS_UPDATED', label: 'Status Atualizado' },
    { value: 'TICKET_ASSIGNED', label: 'Ticket Atribuído' },
    { value: 'TICKET_FINALIZED', label: 'Ticket Finalizado' },
    { value: 'SLA_WARNING', label: 'Alerta de SLA' },
    { value: 'SLA_EXCEEDED', label: 'SLA Vencido' },
  ];

  const variables = [
    { var: '{{customerName}}', desc: 'Nome do cliente' },
    { var: '{{customerEmail}}', desc: 'Email do cliente' },
    { var: '{{ticketProtocol}}', desc: 'Protocolo do ticket' },
    { var: '{{ticketProduct}}', desc: 'Produto do ticket' },
    { var: '{{ticketCategory}}', desc: 'Categoria do ticket' },
    { var: '{{ticketStatus}}', desc: 'Status do ticket' },
    { var: '{{ticketPriority}}', desc: 'Prioridade do ticket' },
    { var: '{{ticketDescription}}', desc: 'Descrição do problema' },
    { var: '{{previousStatus}}', desc: 'Status anterior' },
    { var: '{{newStatus}}', desc: 'Novo status' },
    { var: '{{assignedToName}}', desc: 'Nome do atendente' },
    { var: '{{companyName}}', desc: 'Nome da empresa' },
    { var: '{{companyEmail}}', desc: 'Email da empresa' },
    { var: '{{companyPhone}}', desc: 'Telefone da empresa' },
    { var: '{{portalUrl}}', desc: 'Link do portal' },
    { var: '{{primaryColor}}', desc: 'Cor primária' },
    { var: '{{secondaryColor}}', desc: 'Cor secundária' },
    { var: '{{textColor}}', desc: 'Cor do texto' },
    { var: '{{signature}}', desc: 'Assinatura' },
    { var: '{{footerText}}', desc: 'Texto do rodapé' },
  ];

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany && selectedEvent) {
      loadTemplate(selectedEvent);
    }
  }, [selectedCompany, selectedEvent]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const { data } = await api.get('/companies');
      setCompanies(data.items || data || []);
      if (data.items?.length > 0 || data?.length > 0) {
        setSelectedCompany((data.items || data)[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadTemplate = async (eventType) => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      const { data } = await api.get(`/email-templates/${eventType}`, {
        params: { companyId: selectedCompany }
      });
      if (data) {
        setTemplate({
          name: data.name || '',
          subject: data.subject || '',
          htmlContent: data.htmlContent || '',
        });
      } else {
        setTemplate({ name: '', subject: '', htmlContent: '' });
      }
    } catch (error) {
      console.error('Erro ao carregar template:', error);
      setTemplate({ name: '', subject: '', htmlContent: '' });
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async () => {
    if (!selectedCompany) {
      toast.error('Selecione uma empresa');
      return;
    }

    if (!template.subject || !template.htmlContent) {
      toast.error('Preencha o assunto e o conteúdo HTML');
      return;
    }

    setSaving(true);
    try {
      await api.post('/email-templates', {
        companyId: selectedCompany,
        eventType: selectedEvent,
        name: template.name || events.find(e => e.value === selectedEvent)?.label,
        subject: template.subject,
        htmlContent: template.htmlContent,
      });
      toast.success('Template salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar template:', error);
      toast.error('Erro ao salvar template');
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('htmlContent');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = template.htmlContent;
    const before = text.substring(0, start);
    const after = text.substring(end);
    
    setTemplate({
      ...template,
      htmlContent: before + variable + after,
    });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Mail className="w-6 h-6 text-blue-600" /> Templates de Email
        </h1>
        <p className="text-sm text-slate-500">Personalize os templates de email do sistema.</p>
      </div>

      <Card className="p-6 space-y-4">
        <div>
          <Label className="flex items-center gap-2">
            <Building2 className="w-4 h-4" /> Empresa
          </Label>
          {loadingCompanies ? (
            <div className="text-sm text-slate-500">Carregando empresas...</div>
          ) : (
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma empresa" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <p className="text-xs text-slate-500 mt-1">
            Selecione a empresa para configurar os templates de email
          </p>
        </div>

        <div>
          <Label>Evento</Label>
          <Select value={selectedEvent} onValueChange={setSelectedEvent} disabled={!selectedCompany}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {events.map((e) => (
                <SelectItem key={e.value} value={e.value}>
                  {e.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!selectedCompany ? (
          <div className="py-8 text-center text-slate-500">
            Selecione uma empresa para começar
          </div>
        ) : loading ? (
          <div className="py-8 text-center text-slate-500">Carregando template...</div>
        ) : (
          <>
            <div>
              <Label>Nome do Template (opcional)</Label>
              <Input
                value={template.name}
                onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                placeholder="Ex: Email de Boas-Vindas"
              />
            </div>

            <div>
              <Label>Assunto do Email</Label>
              <Input
                value={template.subject}
                onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
                placeholder="Ex: Ticket {{ticketProtocol}} - Criado"
              />
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Code className="w-4 h-4" /> Variáveis Disponíveis
              </Label>
              <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200 max-h-40 overflow-y-auto">
                <div className="grid grid-cols-2 gap-2">
                  {variables.map((v) => (
                    <button
                      key={v.var}
                      onClick={() => insertVariable(v.var)}
                      className="text-left text-xs bg-white border border-slate-200 px-2 py-1.5 rounded hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      title={v.desc}
                    >
                      <code className="text-blue-600 font-mono">{v.var}</code>
                      <span className="text-slate-500 ml-1">- {v.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Clique em uma variável para inseri-la no template
              </p>
            </div>

            <div>
              <Label>Conteúdo HTML</Label>
              <Textarea
                id="htmlContent"
                value={template.htmlContent}
                onChange={(e) => setTemplate({ ...template, htmlContent: e.target.value })}
                rows={20}
                className="font-mono text-xs"
                placeholder="Cole ou escreva o HTML do template aqui..."
              />
              <p className="text-xs text-slate-500 mt-1">
                Use as variáveis acima para personalizar o conteúdo. Exemplo: Olá {'{{customerName}}'}!
              </p>
            </div>

            <div className="flex gap-2">
              <Button onClick={saveTemplate} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Salvando...' : 'Salvar Template'}
              </Button>
              <Button variant="outline" disabled>
                <Eye className="w-4 h-4 mr-2" /> Visualizar (em breve)
              </Button>
            </div>
          </>
        )}
      </Card>

      <Card className="p-4 bg-blue-50 border-blue-200">
        <h3 className="font-medium text-blue-900 mb-2">💡 Dicas</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Use HTML válido para garantir compatibilidade com clientes de email</li>
          <li>• Teste o template enviando um email de teste antes de ativar</li>
          <li>• As cores do layout da empresa serão aplicadas automaticamente</li>
          <li>• Mantenha o template responsivo para dispositivos móveis</li>
        </ul>
      </Card>
    </div>
  );
}
